import { useEffect, useState } from "react";
import {
  Button,
  DropDownInput,
  AddressInputField,
  InputField,
  DateInput,
} from "@/components";
import { AiOutlineCheck } from "react-icons/ai";
import { SheetClose } from "@/components/ui/sheet"; // Import SheetClose
import { useAlert } from "@/";
import { resolveNDR } from "../../app/slices";
import { useDispatch, useSelector } from "react-redux";

const INITIAL_FORM_STATE = {
  ndrAction: "",
  reAttemptDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Default to 24 hours later
  remark: "",
  line1: "",
  phone: "",
};

const ndrActionOptions = [
  { label: "Re-attempt", value: "Reschedule" },
  { label: "Update Address", value: "Change Address" },
  { label: "Update Phone", value: "Change Phone" },
  { label: "RTO", value: "RTO" },
  { label: "Hold NDR (For 48 hours)", value: "Hold-NDR" },
];

const NDRForm = ({ onClose, ids = [] }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const { alert, AlertDialog } = useAlert();
  const dispatch = useDispatch();
  const isNdrLoading = useSelector((state) => state.ndr.loading);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM_STATE);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form Data", formData);

    // Base validation
    if (!formData.ndrAction || !formData.remark) {
      await alert("Please fill in all required fields.");
      return;
    }

    // Conditional validation based on action
    if (formData.ndrAction === "Change Address") {
      if (!formData.line1) {
        await alert("Please fill in address.");
        return;
      }
    }

    if (formData.ndrAction === "Change Phone") {
      const phoneRegex = /^[6-9]\d{9}$/; // Indian phone number validation
      if (!formData.phone || !phoneRegex.test(formData.phone)) {
        await alert(
          "Please provide a valid 10-digit phone number starting with 6-9."
        );
        return;
      }
    }

    const body = {
      shipmentIds: ids,
      action: formData.ndrAction,
      remarks: formData.remark,
      actionDetails: {
        // Conditional fields
        ...(formData.ndrAction === "Change Address" && {
          address: {
            lines: [{ line1: formData.line1, line2: "" }],
            city: "",
            state: "",
            pincode: "",
            landmark: "",
            customerName: "",
          },
        }),
        ...(formData.ndrAction === "Change Phone" && {
          contactInfo: {
            phone: formData.phone,
            email: "",
            alternatePhone: "",
          },
        }),
        ...(formData.ndrAction === "Reschedule" && {
          rescheduleDate: formData.reAttemptDate,
        }),
        ...(formData.ndrAction === "Hold-NDR" && {
          holdReason: formData.remark,
          additionalInfo: "",
        }),
        reAttemptDate: formData.reAttemptDate,
      },
    };

    try {
      console.log("NDR Body", body);
      const response = await dispatch(resolveNDR(body));
      if (resolveNDR.fulfilled.match(response)) {
        await alert("NDR action taken successfully!");
        onClose();
      } else if (resolveNDR.rejected.match(response)) {
        await alert(response.payload || "Failed to resolve NDR");
      }
    } catch (error) {
      await alert(
        "Failed to resolve NDR",
        error.message || error || "An error occurred"
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <DropDownInput
          label="NDR Action"
          placeholder="Select NDR Action"
          options={ndrActionOptions}
          value={formData.ndrAction}
          onChange={(value) => {
            // Update action while preserving other fields
            setFormData((prev) => ({
              ...prev,
              ndrAction: value,
              // Reset action-specific fields
              line1: "",
              phone: "",
            }));
          }}
          optionClassName="hover:bg-primary/10 cursor-pointer"
          selectedClassName="bg-primary/10"
        />

        {/* Conditional rendering blocks */}

        {formData.ndrAction === "Change Address" && (
          <InputField
            label="Address (only initial line)"
            placeholder="Enter new address"
            value={formData.line1}
            onChange={(value) => handleChange("line1", value)}
          />
        )}

        {formData.ndrAction === "Change Phone" && (
          <InputField
            label="Phone Number"
            name="phone"
            placeholder="Enter new phone number"
            value={formData.phone}
            onChange={(value) => handleChange("phone", value)}
          />
        )}

        {/* Always show re-attempt date */}
        <DateInput
          label="Re-attempt Date"
          name="reAttemptDate"
          value={formData.reAttemptDate}
          showTimePicker={true}
          onChange={(value) => handleChange("reAttemptDate", value)}
        />

        {/* Always show remark field */}
        <AddressInputField
          label="Remark"
          name="remark"
          placeholder="Enter remark"
          value={formData.remark}
          onChange={(value) => handleChange("remark", value)}
        />
      </div>

      <div className="flex gap-2 justify-end items-center">
        <SheetClose asChild>
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={handleReset}
          >
            Cancel
          </Button>
        </SheetClose>
        <Button
          variant="default"
          size="sm"
          icon={AiOutlineCheck}
          type="submit"
          isLoading={isNdrLoading}
        >
          Submit
        </Button>
      </div>
      <AlertDialog />
    </form>
  );
};

export default NDRForm;
